import apiClient from '@/api/index.js'

const state = {
  inventory: [],
  inventoryItem: null,
  loading: false,
  error: null,
  stats: {
    totalProducts: 0,
    recentTransactions: 0,
    totalLocations: 0,
    alerts: 0,
    distribution: []
  },
  recentActivities: [],
  lowStockProducts: []
}

const getters = {
  allInventory: state => state.inventory,
  inventoryItemById: state => id => state.inventory.find(item => item.id === id),
  loading: state => state.loading,
  inventoryStats: state => state.stats,
  recentActivities: state => state.recentActivities,
  lowStockProducts: state => state.lowStockProducts
}

const actions = {
  async fetchInventory({ commit }) {
    console.log('Iniciando fetchInventory')
    commit('SET_LOADING', true)
    try {
      // Obtener inventario y productos
      const [inventoryResponse, productsResponse] = await Promise.all([
        apiClient.getInventory(),
        apiClient.getProducts()
      ])
      
      console.log('🔍 DEBUG STORE - Respuesta de inventario:')
      console.log('Total items recibidos del API:', inventoryResponse.data.length)
      console.log('Total productos recibidos:', productsResponse.data.length)

      // Crear un mapa de productos por ID para búsqueda rápida
      const productsMap = productsResponse.data.reduce((acc, product) => {
        acc[product.id] = product
        return acc
      }, {})
      
      console.log('Mapa de productos:', productsMap)

      // Combinar datos de inventario con productos
      const combinedInventory = inventoryResponse.data.map(item => {
        console.log('Procesando item de inventario:', item)
        const fullProduct = productsMap[item.product.id]
        console.log('Producto completo encontrado:', fullProduct)
        
        if (!fullProduct) {
          console.warn('No se encontró el producto completo para el ID:', item.product.id)
        }

        // IMPORTANTE: El backend puede retornar location.id como zone_id, no location_id real
        // Necesitamos usar el location_id original del InventoryItem, no el id de la estructura location
        // El backend construye location desde zone, así que location.id puede ser zone_id
        // Para el ajuste necesitamos el location_id real del InventoryItem
        
        return {
          id: item.id,
          // Propiedades planas para la tabla
          product_id: item.product.id,
          // Usar location.id pero también guardar la estructura completa para referencia
          location_id: item.location?.id || null, // Puede ser zone_id, no location_id real
          product_sku: item.product.sku,
          product_name: item.product.name,
          location_code: item.location?.code || 'N/A',
          // Propiedades originales
          sku: item.product.sku,
          name: item.product.name,
          description: item.product.description,
          category: fullProduct?.category || item.product.category || 'Sin categoría',
          current_stock: item.quantity,
          min_stock: fullProduct?.min_stock || 0,
          location: item.location?.code || 'N/A',
          abc_category: fullProduct?.abc_category || 'C',
          status: fullProduct?.status || 'active',
          unit_of_measure: fullProduct?.unit_of_measure || item.product.unit_of_measure || 'unidad',
          created_at: item.created_at,
          updated_at: item.updated_at,
          expiry_date: item.expiry_date,
          // Campos adicionales del producto
          barcode: fullProduct?.barcode,
          reorder_point: fullProduct?.reorder_point || 0,
          dimensions: fullProduct?.dimensions,
          weight: fullProduct?.weight,
          // Mantener objetos originales para referencia si es necesario
          product: item.product,
          location: item.location, // Estructura completa de location (puede tener zone, warehouse)
          quantity: item.quantity,
          lot_number: item.lot_number ? item.lot_number : 'N/A',
          serial_number: item.serial_number || null, // Número de serie del producto
          // IMPORTANTE: Guardar el inventory_item.id que es el que se usa para el ajuste
          inventory_item_id: item.id
        }
      })

      console.log('🔍 DEBUG STORE - Inventario combinado:')
      console.log('Total items después de mapear:', combinedInventory.length)
      console.log('Primeros 3 items:', combinedInventory.slice(0, 3))
      commit('SET_INVENTORY', combinedInventory)
    } catch (error) {
      console.error('Error en fetchInventory:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  fetchInventoryItem({ commit }, id) {
    commit('SET_LOADING', true)
    apiClient.getInventoryItem(id)
      .then(response => {
        commit('SET_INVENTORY_ITEM', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching inventory item:', error)
      })
  },
  updateInventoryItem({ commit }, { id, adjustment }) {
    commit('SET_LOADING', true)
    // Se asume que para actualizar el inventario se usa el endpoint de ajuste
    return apiClient.adjustInventory(id, adjustment)
      .then(response => {
        // Aquí puedes actualizar la vista con la nueva información recibida del backend
        commit('UPDATE_INVENTORY_ITEM', response.data)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  async getInventoryStats({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getInventoryStats()
      commit('SET_INVENTORY_STATS', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching inventory stats:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async getRecentActivities({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getRecentActivities()
      commit('SET_RECENT_ACTIVITIES', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching recent activities:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async getLowStockProducts({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getLowStockProducts()
      commit('SET_LOW_STOCK_PRODUCTS', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching low stock products:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async adjustInventory({ commit }, adjustment) {
    console.log('[STORE] adjustInventory - Ajuste recibido:', adjustment)
    console.log('[STORE] adjustInventory - adjustment.id (inventory_id):', adjustment.id)
    console.log('[STORE] adjustInventory - adjustmentData a enviar:', {
      adjustment_type: adjustment.adjustment_type || 'set',
      quantity: adjustment.quantity,
      reason: adjustment.reason || 'Ajuste manual',
      notes: adjustment.notes || ''
    })
    
    commit('SET_LOADING', true)
    try {
      // Verificar que tenemos un token antes de hacer la petición
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No hay token de autenticación. Por favor, inicia sesión.')
      }

      const adjustmentData = {
        adjustment_type: adjustment.adjustment_type || 'set',
        quantity: adjustment.quantity,
        reason: adjustment.reason || 'Ajuste manual',
        notes: adjustment.notes || ''
      }
      
      console.log('[STORE] adjustInventory - Llamando a API con inventory_id:', adjustment.id)
      console.log('[STORE] adjustInventory - URL completa será: /inventory/' + adjustment.id + '/adjust')
      
      const response = await apiClient.adjustInventory(adjustment.id, adjustmentData)
      
      console.log('[STORE] adjustInventory - Respuesta recibida:', response.data)
      console.log('[STORE] adjustInventory - response.data.inventory:', response.data.inventory)
      
      // Actualizar el item en el estado
      if (response.data.inventory) {
        // Mapear la estructura del backend a la estructura esperada por el frontend
        const inventoryItem = response.data.inventory
        console.log('[STORE] adjustInventory - inventoryItem recibido:', inventoryItem)
        console.log('[STORE] adjustInventory - inventoryItem.id:', inventoryItem.id)
        
        // Buscar el item actual en el estado para preservar propiedades adicionales
        const currentState = state.inventory || []
        const existingItem = currentState.find(item => item.id === inventoryItem.id)
        console.log('[STORE] adjustInventory - existingItem encontrado:', existingItem)
        
        // Construir el item actualizado combinando el existente con los nuevos datos
        const updatedItem = {
          ...existingItem, // Preservar propiedades del item existente
          id: inventoryItem.id,
          quantity: inventoryItem.quantity,
          lot_number: inventoryItem.lot_number,
          serial_number: inventoryItem.serial_number || existingItem?.serial_number || null, // Preservar serial_number
          updated_at: inventoryItem.updated_at,
          // Actualizar location si viene en la respuesta
          location_id: inventoryItem.location?.id || inventoryItem.location_id || existingItem?.location_id,
          location_code: inventoryItem.location?.code || existingItem?.location_code,
          location: inventoryItem.location || existingItem?.location,
          // Actualizar product si viene en la respuesta
          product_id: inventoryItem.product?.id || inventoryItem.product_id || existingItem?.product_id,
          product_sku: inventoryItem.product?.sku || existingItem?.product_sku,
          product_name: inventoryItem.product?.name || existingItem?.product_name
        }
        
        console.log('[STORE] adjustInventory - updatedItem construido:', updatedItem)
        commit('UPDATE_INVENTORY_ITEM', updatedItem)
      } else {
        console.warn('[STORE] adjustInventory - No se recibió inventory en la respuesta')
      }
      
      return response.data
    } catch (error) {
      console.error('Error adjusting inventory:', error)
      
      // Manejar errores de autenticación específicamente
      if (error.response && error.response.status === 401) {
        throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.')
      } else if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error)
      } else if (error.message) {
        throw error
      } else {
        throw new Error('Error desconocido al ajustar inventario')
      }
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_INVENTORY(state, inventory) {
    console.log('SET_INVENTORY mutation recibió:', inventory)
    state.inventory = inventory
    console.log('Estado actualizado del inventario:', state.inventory)
  },
  SET_INVENTORY_ITEM(state, inventoryItem) {
    state.inventoryItem = inventoryItem
  },
  UPDATE_INVENTORY_ITEM(state, updatedItem) {
    console.log('[STORE] UPDATE_INVENTORY_ITEM - updatedItem recibido:', updatedItem)
    console.log('[STORE] UPDATE_INVENTORY_ITEM - updatedItem.id:', updatedItem.id)
    console.log('[STORE] UPDATE_INVENTORY_ITEM - state.inventory.length:', state.inventory.length)
    
    const index = state.inventory.findIndex(item => item.id === updatedItem.id)
    console.log('[STORE] UPDATE_INVENTORY_ITEM - index encontrado:', index)
    
    if (index !== -1) {
      console.log('[STORE] UPDATE_INVENTORY_ITEM - Item antes de actualizar:', state.inventory[index])
      state.inventory.splice(index, 1, updatedItem)
      console.log('[STORE] UPDATE_INVENTORY_ITEM - Item después de actualizar:', state.inventory[index])
      console.log('[STORE] UPDATE_INVENTORY_ITEM - Nueva cantidad:', state.inventory[index].quantity)
    } else {
      console.warn('[STORE] UPDATE_INVENTORY_ITEM - No se encontró item con id:', updatedItem.id)
      console.log('[STORE] UPDATE_INVENTORY_ITEM - IDs disponibles:', state.inventory.map(item => item.id))
    }
    
    if (state.inventoryItem && state.inventoryItem.id === updatedItem.id) {
      state.inventoryItem = updatedItem
    }
  },
  SET_INVENTORY_STATS(state, stats) {
    state.stats = stats
  },
  SET_RECENT_ACTIVITIES(state, activities) {
    state.recentActivities = activities
  },
  SET_LOW_STOCK_PRODUCTS(state, products) {
    state.lowStockProducts = products
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
